import { BasicButton, Stack, Text } from "@certn/cascadia";
import {
  GhostIconButton,
  GridItem,
  InputWrapper,
  Select,
  SelectItem,
  TextField,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { FieldArray, useField, useFormikContext } from "formik";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  MAX_REFERENCE_QUANTITY,
  type ReferenceCheckTemplateFormValues,
} from "./utils";

function ReferenceQuestionTextField({
  name,
  label,
  testID,
  isDisabled,
  isRequired,
}: {
  name: string;
  label: string;
  testID: string;
  isDisabled: boolean;
  isRequired?: boolean;
}) {
  const [field, meta, helpers] = useField(name);
  const { submitCount } = useFormikContext();
  const showError = meta.touched || submitCount > 0;

  return (
    <InputWrapper span="auto">
      <TextField
        value={field.value ?? ""}
        name={name}
        label={label}
        isInvalid={Boolean(showError && meta.error)}
        errors={showError ? meta.error : undefined}
        onChange={(value) => helpers.setValue(value)}
        onBlur={() => helpers.setTouched(true)}
        isRequired={isRequired}
        testID={testID}
        isDisabled={isDisabled}
      />
    </InputWrapper>
  );
}

function QuestionList({
  questions,
  fieldPrefix,
  onAdd,
  onRemove,
  isDisabled,
  testIdPrefix,
}: {
  questions: string[];
  fieldPrefix: string;
  onAdd: () => void;
  onRemove: (index: number) => void;
  isDisabled: boolean;
  testIdPrefix: string;
}) {
  const { t } = useTranslation();

  return (
    <>
      {questions.map((_, index) => (
        <GridItem key={index}>
          <div className={styles.questionRow}>
            <div className={styles.questionInput}>
              <ReferenceQuestionTextField
                name={`${fieldPrefix}.${index}`}
                label={t("Question {{number}}", { number: index + 1 })}
                testID={`${testIdPrefix}-question-${index}`}
                isDisabled={isDisabled}
                isRequired
              />
            </div>
            {!isDisabled && questions.length > 1 && (
              <GhostIconButton
                testID={`${testIdPrefix}-remove-${index}`}
                size="small"
                icon="delete"
                aria-label={t("Remove question")}
                onPress={() => onRemove(index)}
              />
            )}
          </div>
        </GridItem>
      ))}
      {!isDisabled && (
        <GridItem>
          <BasicButton
            testID={`${testIdPrefix}-add-question`}
            onPress={onAdd}
            size="small"
          >
            {t("Add question")}
          </BasicButton>
        </GridItem>
      )}
    </>
  );
}

function ReferenceQuantityField({
  name,
  label,
  testID,
  isDisabled,
}: {
  name: string;
  label: string;
  testID: string;
  isDisabled: boolean;
}) {
  const { t } = useTranslation();
  const [field, meta, helpers] = useField<number>(name);
  const { submitCount } = useFormikContext();
  const showError = meta.touched || submitCount > 0;

  const options = useMemo(
    () =>
      Array.from({ length: MAX_REFERENCE_QUANTITY + 1 }, (_, i) => ({
        id: String(i),
        value: String(i),
        label: String(i),
      })),
    []
  );

  return (
    <Select
      items={options}
      label={label}
      value={String(field.value ?? 0)}
      onChange={(value) => helpers.setValue(Number(value))}
      onBlur={() => helpers.setTouched(true)}
      isDisabled={isDisabled}
      isInvalid={Boolean(showError && meta.error)}
      errors={showError ? meta.error : undefined}
      testID={testID}
    >
      {(item) => (
        <SelectItem
          id={item.id}
          key={item.id}
          textValue={item.label}
          data-testid={`${testID}-option-${item.value}`}
        >
          {item.label === "1"
            ? t("1 reference")
            : t("{{count}} references", { count: Number(item.label) })}
        </SelectItem>
      )}
    </Select>
  );
}

export function TemplateNameField({
  isDisabled = false,
}: {
  isDisabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <GridItem>
      <TextInputFormikField
        name="name"
        isRequired
        label={t("Template name")}
        description={t(
          "Used internally to identify the template. The applicant will not see this."
        )}
        testID="template-name"
        isDisabled={isDisabled}
      />
    </GridItem>
  );
}

export function ReferenceQuantitiesSection({
  isDisabled = false,
  showHeading = true,
}: {
  isDisabled?: boolean;
  showHeading?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      {showHeading && (
        <GridItem>
          <Stack gap="space-0">
            <Text style="form/section-heading">
              {t("Reference quantities")}
            </Text>
            <Text style="form/body-secondary">
              {t(
                "Choose how many professional and personal references to collect. Select at least one."
              )}
            </Text>
          </Stack>
        </GridItem>
      )}
      <GridItem>
        <div className={styles.quantityRow}>
          <div className={styles.quantityField}>
            <ReferenceQuantityField
              name="professional_quantity"
              label={t("Professional references")}
              testID="professional-quantity"
              isDisabled={isDisabled}
            />
          </div>
          <div className={styles.quantityField}>
            <ReferenceQuantityField
              name="personal_quantity"
              label={t("Personal references")}
              testID="personal-quantity"
              isDisabled={isDisabled}
            />
          </div>
        </div>
      </GridItem>
    </>
  );
}

export function ProfessionalQuestionsSection({
  values,
  isDisabled = false,
}: {
  values: ReferenceCheckTemplateFormValues;
  isDisabled?: boolean;
}) {
  const { t } = useTranslation();
  if ((values.professional_quantity ?? 0) <= 0) {
    return null;
  }
  return (
    <>
      <GridItem>
        <Stack gap="space-0">
          <Text style="form/section-heading">
            {t("Professional reference questions")}
          </Text>
          <Text style="form/body-secondary">
            {t(
              "These questions will be asked when verifying professional references."
            )}
          </Text>
        </Stack>
      </GridItem>

      <FieldArray name="professional_questions">
        {({ push, remove }) => (
          <QuestionList
            questions={values.professional_questions}
            fieldPrefix="professional_questions"
            onAdd={() => push("")}
            onRemove={remove}
            isDisabled={isDisabled}
            testIdPrefix="professional"
          />
        )}
      </FieldArray>
    </>
  );
}

export function PersonalQuestionsSection({
  values,
  isDisabled = false,
}: {
  values: ReferenceCheckTemplateFormValues;
  isDisabled?: boolean;
}) {
  const { t } = useTranslation();
  if ((values.personal_quantity ?? 0) <= 0) {
    return null;
  }
  return (
    <>
      <GridItem>
        <Stack gap="space-0">
          <Text style="form/section-heading">
            {t("Personal reference questions")}
          </Text>
          <Text style="form/body-secondary">
            {t(
              "These questions will be asked when verifying personal references."
            )}
          </Text>
        </Stack>
      </GridItem>

      <FieldArray name="personal_questions">
        {({ push, remove }) => (
          <QuestionList
            questions={values.personal_questions}
            fieldPrefix="personal_questions"
            onAdd={() => push("")}
            onRemove={remove}
            isDisabled={isDisabled}
            testIdPrefix="personal"
          />
        )}
      </FieldArray>
    </>
  );
}

interface ReferenceCheckTemplateFormProps {
  values: ReferenceCheckTemplateFormValues;
  isDisabled?: boolean;
}

export function ReferenceCheckTemplateForm({
  values,
  isDisabled = false,
}: ReferenceCheckTemplateFormProps) {
  return (
    <>
      <TemplateNameField />
      <ReferenceQuantitiesSection isDisabled={isDisabled} />
      <ProfessionalQuestionsSection values={values} isDisabled={isDisabled} />
      <PersonalQuestionsSection values={values} isDisabled={isDisabled} />
    </>
  );
}

const styles = {
  questionRow: css({
    display: "flex",
    alignItems: "flex-start",
    gap: "var(--space-2)",
  }),
  questionInput: css({
    flex: 1,
  }),
  quantityRow: css({
    display: "flex",
    gap: "var(--space-3)",
    flexWrap: "wrap",
  }),
  quantityField: css({
    flex: "1 1 180px",
    minWidth: "180px",
  }),
};
